import { CalendarDate, type Time, parseDate, parseTime } from "@internationalized/date";
import { useLingui } from "@lingui/react/macro";
import { formatDuration } from "@offstreetparking/design-system/formatters";
import { Button, FormError, useEnhancedForm } from "@offstreetparking/design-system/forms";
import { DurationPicker, FormDateInput, FormTimeInput } from "@offstreetparking/design-system/forms/datetime/lazy";
import { clampTime, stringToTimeValue } from "@offstreetparking/design-system/forms/datetime/utils";
import { ArrowRightFromLineIcon } from "@offstreetparking/design-system/icons/ArrowRightFromLineIcon";
import { ArrowRightToLineIcon } from "@offstreetparking/design-system/icons/ArrowRightToLineIcon";
import { Text } from "@offstreetparking/design-system/typography";
import { DAYS_OF_WEEK } from "@offstreetparking/domain/value";
import { DateTime } from "luxon";
import { useState } from "react";
import { FormProvider } from "react-hook-form";
import { CapacityFullBadge } from "../../../dataDisplay/CapacityFullBadge";
import { ResponsiveOverlay } from "../../../overlays/ResponsiveOverlay";
import type { WizardParkingChoice } from "../../PermitContextProvider/types";
import { DurationSelection } from "./components/DurationSelection";
import { ExpiryTimeHelper } from "./components/ExpiryTimeHelper";
import { ExpiryTimeOptions } from "./components/ExpiryTimeOptions";
import { StartTimeHelper } from "./components/StartTimeHelper";
import { StartTimeOptions } from "./components/StartTimeOptions";
import { computeMaxEndDate } from "./computeMaxEndDate";
import { useDurationCalculations } from "./hooks/useDurationCalculations";
import { useEndTimeRecalculation } from "./hooks/useEndTimeRecalculation";
import { useStartOptionsEnabled } from "./hooks/useStartOptionsEnabled";
import { isStartTimeSetToNow } from "./hooks/useStartTimeNowCheck";
import type { PermitTimeFormData } from "./permitTimeSchema";
import { makeTimeSelectionSchema } from "./timeSelectionSchema";

type TimeSelectionFormProps = {
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
	optionName: string;
	title: string;
	permitIntervals: PermitTimeFormData["choices"][number]["permitIntervals"];
	wizardSettings: WizardParkingChoice["time"];
	timezone: string;
	onSubmit: (
		intervals: {
			startDate: string;
			startTime: string;
			endDate?: string;
			endTime?: string;
		}[],
	) => void;
	capacityErrorMessage?: string;
};
export const TimeSelectionForm = ({
	isOpen,
	onOpenChange,
	optionName,
	title,
	permitIntervals,
	wizardSettings,
	timezone,
	onSubmit,
	capacityErrorMessage,
}: TimeSelectionFormProps) => {
	const { i18n, t } = useLingui();
	const [activeExpiryOption, setActiveExpiryOption] = useState<"time" | "duration" | "step">(
		wizardSettings.stepSize !== undefined ? "step" : "time",
	);
	const [startTimeMode, setStartTimeMode] = useState<"now" | "pick">(() => {
		const firstInterval = permitIntervals[0];
		const startDate = firstInterval?.startDate ? parseDate(firstInterval.startDate) : undefined;
		const startTime = firstInterval?.startTime ? parseTime(firstInterval.startTime) : undefined;
		return wizardSettings.startDate.enabled &&
			wizardSettings.startTime.enabled &&
			isStartTimeSetToNow(startDate, startTime, timezone)
			? "now"
			: "pick";
	});
	const methods = useEnhancedForm({
		schema: makeTimeSelectionSchema({ wizardSettings, timezone }),
		initialValues: {
			intervals: (permitIntervals.length > 0 ? permitIntervals : [{}]).map((i) => ({
				startDate: i.startDate ? parseDate(i.startDate) : undefined,
				startTime: i.startTime ? parseTime(i.startTime) : clampTime(wizardSettings.startTime),
				endDate: i.endDate ? parseDate(i.endDate) : undefined,
				endTime: i.endTime
					? parseTime(i.endTime)
					: wizardSettings.neverExpires
						? undefined
						: clampTime(wizardSettings.endTime),
				durationInMinutes:
					wizardSettings.stepSize !== undefined && i.startDate && i.startTime && i.endDate && i.endTime
						? DateTime.fromISO(`${i.endDate}T${i.endTime}`, { zone: timezone })
								.diff(DateTime.fromISO(`${i.startDate}T${i.startTime}`, { zone: timezone }))
								.as("minutes")
						: undefined,
			})),
		},
		t,
	});
	const isStartOptionsEnabled = useStartOptionsEnabled(wizardSettings, timezone);
	const { calculateMaxDurationMinutes, calculateMinDurationMinutes } = useDurationCalculations(
		wizardSettings,
		methods,
		timezone,
	);
	const { recalculateEndTimeFromDuration } = useEndTimeRecalculation(wizardSettings, timezone, methods);
	return (
		<ResponsiveOverlay isOpen={isOpen} onOpenChange={onOpenChange} title={title} description={optionName}>
			<div className="space-y-4">
				<div className="-mx-4 mt-4 h-[5px] bg-neutral-faded" />
				{capacityErrorMessage && !methods.formState.isDirty && (
					<CapacityFullBadge label={capacityErrorMessage} />
				)}

				{isOpen && (
					<FormProvider {...methods}>
						{methods.watch("intervals").map((_, index) => (
							// biome-ignore lint/suspicious/noArrayIndexKey: safe index
							<div key={index} className="space-y-4">
								{/* start time section */}
								<div>
									<div className="mb-2 flex items-center gap-2">
										<ArrowRightFromLineIcon className="h-4 w-4" />
										<Text size="md" weight="bold">{t`Start Time`}</Text>
									</div>

									<StartTimeHelper wizardSettings={wizardSettings} />

									{isStartOptionsEnabled && (
										<StartTimeOptions
											isStartTimeSetToNow={startTimeMode === "now"}
											onNowClicked={() => {
												const now = DateTime.now().setZone(timezone);
												const startDate = parseDate(now.toISODate()!);
												const startTime = parseTime(now.toFormat("HH:mm:ss"));
												methods.setValue(`intervals.${index}.startDate`, startDate, {
													shouldValidate: true,
												});
												methods.setValue(`intervals.${index}.startTime`, startTime, {
													shouldValidate: true,
												});
												recalculateEndTimeFromDuration(index, startDate, startTime);
												setStartTimeMode("now");
											}}
											onPickTimeClicked={() => {
												setStartTimeMode("pick");
											}}
										/>
									)}

									<div className="flex items-start gap-2">
										<FormDateInput
											name={`intervals.${index}.startDate`}
											onCalendarDateSelect={(date) => {
												if (date && date instanceof CalendarDate) {
													methods.setValue(`intervals.${index}.startDate`, date, {
														shouldValidate: true,
													});
													const startTime = methods.watch(`intervals.${index}.startTime`);
													if (startTime) {
														recalculateEndTimeFromDuration(index, date, startTime);
													}
												}
												if (date) {
													(
														document.querySelector(
															`intervals\\.${index}\\.startTime > div`,
														) as HTMLElement
													)?.focus();
												}
											}}
											onChange={(date) => {
												if (date && date instanceof CalendarDate) {
													methods.setValue(`intervals.${index}.startDate`, date);
													const startTime = methods.watch(`intervals.${index}.startTime`);
													if (startTime) {
														recalculateEndTimeFromDuration(index, date, startTime);
													}
												}
											}}
											isDateUnavailable={(x) => {
												if (!wizardSettings.startDate.allowedDays) {
													return false;
												}
												const date = DateTime.fromObject(
													{
														year: x.year,
														month: x.month,
														day: x.day,
													},
													{
														zone: timezone,
													},
												);
												const day = DAYS_OF_WEEK[date.weekday - 1]!;
												return !wizardSettings.startDate.allowedDays.includes(day);
											}}
											minValue={
												wizardSettings.startDate.min
													? parseDate(wizardSettings.startDate.min)
													: undefined
											}
											maxValue={
												wizardSettings.startDate.max
													? parseDate(wizardSettings.startDate.max)
													: undefined
											}
											isDisabled={startTimeMode === "now" || !wizardSettings.startDate.enabled}
											i18n={{
												applyButton: t`Apply`,
												cancelButton: t`Cancel`,
												clearButton: t`Clear`,
												openCalendar: t`Open calendar`,
												previousDayButton: t`Previous day`,
												nextDayButton: t`Next day`,
												previousMonthButton: t`Previous month`,
												nextMonthButton: t`Next month`,
											}}
											aria-label={t`Start Time`}
										/>

										<Text className="mt-2">{t`at`}</Text>

										<FormTimeInput
											id="startTime"
											name={`intervals.${index}.startTime`}
											aria-invalid={
												!!methods.formState.errors?.intervals?.[index]?.startTime?.message
											}
											isDisabled={startTimeMode === "now" || !wizardSettings.startTime.enabled}
											minValue={
												wizardSettings.startTime.min
													? stringToTimeValue(wizardSettings.startTime.min)!
													: undefined
											}
											maxValue={
												wizardSettings.startTime.max
													? stringToTimeValue(wizardSettings.startTime.max)!
													: undefined
											}
											onChange={(time) => {
												const startDate = methods.watch(`intervals.${index}.startDate`);
												if (startDate && time) {
													recalculateEndTimeFromDuration(index, startDate, time as Time);
												}
											}}
										/>
									</div>
								</div>

								{/* end time section */}
								{!wizardSettings.neverExpires && (
									<div>
										<div className="mb-2 flex items-center gap-2">
											<ArrowRightToLineIcon className="h-4 w-4" />
											<Text size="md" weight="bold">
												{t`Expiry Time`}
											</Text>
										</div>

										<ExpiryTimeHelper wizardSettings={wizardSettings} />

										{wizardSettings.endTime.enabled && activeExpiryOption !== "step" && (
											<ExpiryTimeOptions
												activeExpiryOption={activeExpiryOption}
												isDurationDisabled={
													!methods.watch(`intervals.${index}.startDate`) ||
													!methods.watch(`intervals.${index}.startTime`)
												}
												onDurationClicked={() => {
													const startDate = methods.getValues(`intervals.${index}.startDate`);
													const startTime = methods.getValues(`intervals.${index}.startTime`);
													const endDate = methods.getValues(`intervals.${index}.endDate`);
													const endTime = methods.getValues(`intervals.${index}.endTime`);
													if (startDate && startTime && endDate && endTime) {
														const start = DateTime.fromObject(
															{
																year: startDate.year,
																month: startDate.month,
																day: startDate.day,
																hour: startTime.hour,
																minute: startTime.minute,
															},
															{
																zone: timezone,
															},
														);
														const end = DateTime.fromObject(
															{
																year: endDate.year,
																month: endDate.month,
																day: endDate.day,
																hour: endTime.hour,
																minute: endTime.minute,
															},
															{
																zone: timezone,
															},
														);
														const diffMinutes = Math.max(
															0,
															Math.round(end.diff(start, "minutes").minutes),
														);
														methods.setValue(
															`intervals.${index}.durationInMinutes`,
															diffMinutes,
														);
													} else {
														const minDuration = calculateMinDurationMinutes(index);
														methods.setValue(
															`intervals.${index}.durationInMinutes`,
															minDuration ?? 0,
														);
														if (startDate && startTime) {
															recalculateEndTimeFromDuration(index, startDate, startTime);
														}
													}
													setActiveExpiryOption("duration");
												}}
												onPickTimeClicked={() => {
													setActiveExpiryOption("time");
												}}
											/>
										)}

										{["time", "step"].includes(activeExpiryOption) && (
											<div className="space-y-4">
												<div className="flex items-start gap-2">
													<FormDateInput
														name={`intervals.${index}.endDate`}
														rangeStartValue={methods.watch(`intervals.${index}.startDate`)}
														onCalendarDateSelect={(date) => {
															if (date && date instanceof CalendarDate) {
																methods.setValue(`intervals.${index}.endDate`, date, {
																	shouldValidate: true,
																});
															}
															if (date) {
																(
																	document.querySelector(
																		`intervals\\.${index}\\.endTime > div`,
																	) as HTMLElement
																)?.focus();
															}
														}}
														onChange={(date) => {
															if (date && date instanceof CalendarDate) {
																methods.setValue(`intervals.${index}.endDate`, date);
															}
														}}
														isDateUnavailable={(x) => {
															if (!wizardSettings.endDate.allowedDays) {
																return false;
															}
															const date = DateTime.fromObject(
																{
																	year: x.year,
																	month: x.month,
																	day: x.day,
																},
																{
																	zone: timezone,
																},
															);
															const day = DAYS_OF_WEEK[date.weekday - 1]!;
															return !wizardSettings.endDate.allowedDays.includes(day);
														}}
														minValue={(() => {
															const startDate = methods.watch(
																`intervals.${index}.startDate`,
															);
															const startTime = methods.watch(
																`intervals.${index}.startTime`,
															);
															const endDateMin = wizardSettings.endDate.min
																? parseDate(wizardSettings.endDate.min)
																: undefined;
															if (
																!wizardSettings.duration.min &&
																!startDate &&
																!startTime
															) {
																return endDateMin;
															}
															if (!wizardSettings.duration.min && startDate) {
																if (endDateMin) {
																	return startDate.compare(endDateMin) > 0
																		? startDate
																		: endDateMin;
																}
																return startDate;
															}
															if (wizardSettings.duration.min && startDate && startTime) {
																const startDateTime = DateTime.fromObject(
																	{
																		year: startDate.year,
																		month: startDate.month,
																		day: startDate.day,
																		hour: startTime.hour,
																		minute: startTime.minute,
																	},
																	{ zone: timezone },
																);
																const minEndDateTime = startDateTime.plus({
																	minutes: wizardSettings.duration.min
																		? wizardSettings.duration.min
																		: 0,
																});
																return parseDate(minEndDateTime.toFormat("yyyy-MM-dd"));
															}
															return endDateMin;
														})()}
														maxValue={computeMaxEndDate({
															wizardSettings,
															startDate: methods.watch(`intervals.${index}.startDate`),
															startTime: methods.watch(`intervals.${index}.startTime`),
															timezone,
														})}
														i18n={{
															applyButton: t`Apply`,
															cancelButton: t`Cancel`,
															clearButton: t`Clear`,
															openCalendar: t`Open calendar`,
															previousDayButton: t`Previous day`,
															nextDayButton: t`Next day`,
															previousMonthButton: t`Previous month`,
															nextMonthButton: t`Next month`,
														}}
														isDisabled={
															!wizardSettings.endDate.enabled ||
															wizardSettings.stepSize !== undefined
														}
														aria-label={t`Expiry Time`}
													/>

													<Text className="mt-2">{t`at`}</Text>

													<FormTimeInput
														id="endTime"
														name={`intervals.${index}.endTime`}
														aria-invalid={
															!!methods.formState.errors?.intervals?.[index]?.endTime
																?.message
														}
														isDisabled={
															!wizardSettings.endTime.enabled ||
															wizardSettings.stepSize !== undefined
														}
														minValue={(() => {
															if (wizardSettings.endTime.min) {
																return stringToTimeValue(wizardSettings.endTime.min)!;
															}
															const startDate = methods.watch(
																`intervals.${index}.startDate`,
															);
															const startTime = methods.watch(
																`intervals.${index}.startTime`,
															);
															const endDate = methods.watch(`intervals.${index}.endDate`);
															if (
																wizardSettings.duration.min &&
																startDate &&
																startTime &&
																endDate
															) {
																const startDateTime = DateTime.fromObject(
																	{
																		year: startDate.year,
																		month: startDate.month,
																		day: startDate.day,
																		hour: startTime.hour,
																		minute: startTime.minute,
																	},
																	{ zone: timezone },
																);
																const minEndDateTime = startDateTime.plus({
																	minutes: wizardSettings.duration.min,
																});
																const minEndDate = parseDate(
																	minEndDateTime.toFormat("yyyy-MM-dd"),
																);
																if (endDate.compare(minEndDate) === 0) {
																	return parseTime(
																		minEndDateTime.toFormat("HH:mm:ss"),
																	);
																}
															}
														})()}
														maxValue={(() => {
															if (wizardSettings.endTime.max) {
																return stringToTimeValue(wizardSettings.endTime.max)!;
															}
															const startDate = methods.watch(
																`intervals.${index}.startDate`,
															);
															const startTime = methods.watch(
																`intervals.${index}.startTime`,
															);
															const endDate = methods.watch(`intervals.${index}.endDate`);
															if (
																wizardSettings.duration.max &&
																startDate &&
																startTime &&
																endDate
															) {
																const startDateTime = DateTime.fromObject(
																	{
																		year: startDate.year,
																		month: startDate.month,
																		day: startDate.day,
																		hour: startTime.hour,
																		minute: startTime.minute,
																	},
																	{ zone: timezone },
																);
																const maxEndDateTime = startDateTime.plus({
																	minutes: wizardSettings.duration.max,
																});
																const maxEndDate = parseDate(
																	maxEndDateTime.toFormat("yyyy-MM-dd"),
																);
																if (endDate.compare(maxEndDate) === 0) {
																	return parseTime(
																		maxEndDateTime.toFormat("HH:mm:ss"),
																	);
																}
															}
														})()}
													/>
												</div>

												{wizardSettings.stepSize && (
													<DurationPicker
														stepSize={wizardSettings.stepSize}
														minDuration={wizardSettings.duration.min}
														maxDuration={calculateMaxDurationMinutes(index)}
														isDisabled={
															!methods.getValues(`intervals.${index}.startDate`) ||
															!methods.getValues(`intervals.${index}.startTime`)
														}
														formatDuration={(minutes) =>
															formatDuration({ duration: minutes, i18n })
														}
														onDurationChange={(minutes) => {
															methods.setValue(
																`intervals.${index}.durationInMinutes`,
																minutes,
															);
															const startDate = methods.getValues(
																`intervals.${index}.startDate`,
															);
															const startTime = methods.getValues(
																`intervals.${index}.startTime`,
															);
															if (startDate && startTime) {
																recalculateEndTimeFromDuration(
																	index,
																	startDate,
																	startTime,
																);
															}
														}}
													/>
												)}
											</div>
										)}

										{activeExpiryOption === "duration" && (
											<DurationSelection
												minutes={methods.watch(`intervals.${index}.durationInMinutes`) ?? 0}
												minMinutes={calculateMinDurationMinutes(index)}
												maxMinutes={calculateMaxDurationMinutes(index)}
												onChange={(totalMinutes) => {
													methods.setValue(
														`intervals.${index}.durationInMinutes`,
														totalMinutes,
														{
															shouldDirty: true,
														},
													);
													const startDate = methods.getValues(`intervals.${index}.startDate`);
													const startTime = methods.getValues(`intervals.${index}.startTime`);
													if (startDate && startTime) {
														recalculateEndTimeFromDuration(index, startDate, startTime);
													}
												}}
											/>
										)}

										{methods.formState.errors?.intervals?.[index]?.durationInMinutes?.message && (
											<FormError
												error={
													methods.formState.errors.intervals[index].durationInMinutes.message
												}
												className="mt-2"
											/>
										)}

										{activeExpiryOption === "duration" &&
											(["endDate", "endTime"] as const).map((field) => {
												const message =
													methods.formState.errors?.intervals?.[index]?.[field]?.message;
												return message ? (
													<FormError key={field} error={message} className="mt-2" />
												) : null;
											})}
									</div>
								)}
							</div>
						))}

						<div className="mt-4 flex justify-end">
							<Button
								intent="primary"
								className="w-full md:w-auto"
								type="button"
								onClick={methods.handleSubmit((values) => {
									methods.reset(values);
									onSubmit(
										values.intervals.map((x) => ({
											startDate: x.startDate?.toString() ?? "",
											endDate: x.endDate?.toString() ?? "",
											startTime: x.startTime?.toString() ?? "",
											endTime: x.endTime?.toString() ?? "",
										})),
									);
								})}
							>
								{t`Done`}
							</Button>
						</div>
					</FormProvider>
				)}
			</div>
		</ResponsiveOverlay>
	);
};
